import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import moment from 'moment';

/**
 * Generate PDF from report data
 * @param {Object} reportData - The full report data
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @returns {Promise<void>}
 */
export const generateReportPDF = async (reportData, element) => {
  if (!element || !reportData) {
    throw new Error('Missing required parameters for PDF generation');
  }

  const companyName = reportData.fullData?.companyDetails?.companyName || 'Report';
  const vatNumber = reportData.fullData?.companyDetails?.vatCode || '';
  const currentDate = moment().format('DD-MM-YYYY');
  const fileName = `${companyName.replace(/\s+/g, '_')}_${vatNumber}_${currentDate}.pdf`;

  try {
    // Create a clone of the element to modify for PDF
    const clonedElement = element.cloneNode(true);
    
    // Apply PDF-specific styles
    clonedElement.style.padding = '20px';
    clonedElement.style.backgroundColor = 'white';
    clonedElement.style.color = 'black';
    
    // Temporarily append to body (hidden)
    clonedElement.style.position = 'absolute';
    clonedElement.style.left = '-9999px';
    clonedElement.style.width = '210mm'; // A4 width
    document.body.appendChild(clonedElement);

    // Convert to canvas
    const canvas = await html2canvas(clonedElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 794, // A4 width in pixels at 96 DPI
    });

    // Remove the cloned element
    document.body.removeChild(clonedElement);

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Save the PDF
    pdf.save(fileName);
    
    return fileName;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Alternative method: Generate a structured PDF from report data
 * This creates a more structured PDF without HTML rendering
 */
export const generateStructuredPDF = (reportData) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 20;

  const companyDetails = reportData.fullData?.companyDetails;
  const ecofin = reportData.fullData?.ecofin;
  const address = reportData.fullData?.address;
  
  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      pdf.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to add text
  const addText = (text, fontSize = 10, isBold = false) => {
    pdf.setFontSize(fontSize);
    if (isBold) {
      pdf.setFont(undefined, 'bold');
    } else {
      pdf.setFont(undefined, 'normal');
    }
    pdf.text(text, 15, yPosition);
    yPosition += fontSize * 0.4;
  };

  // Company name as title
  pdf.setFontSize(20);
  pdf.setFont(undefined, 'bold');
  pdf.text(companyDetails?.companyName || 'Report Aziendale', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Section: Company Details
  addText('INFORMAZIONI AZIENDA', 14, true);
  yPosition += 5;

  if (companyDetails) {
    addText(`Partita IVA: ${companyDetails.vatCode || 'N/A'}`);
    addText(`Codice Fiscale: ${companyDetails.taxCode || 'N/A'}`);
    addText(`CCIAA: ${companyDetails.cciaa || 'N/A'}`);
    addText(`REA: ${companyDetails.reaCode || 'N/A'}`);
  }

  if (address) {
    yPosition += 5;
    addText(`Indirizzo: ${address.streetName || ''}, ${address.zipCode || ''} ${address.town || ''} (${address.province?.code || ''})`);
  }

  // Add company age if available
  const companyDates = reportData.fullData?.companyDates;
  if (companyDates) {
    const foundationDate = companyDates.incorporationDate || companyDates.registrationDate || companyDates.startDate;
    if (foundationDate) {
      const years = moment().diff(moment(foundationDate), 'years');
      yPosition += 5;
      addText(`Età Azienda: ${years} anni (fondata il ${moment(foundationDate).format('DD/MM/YYYY')})`);
    }
  }

  // Section: Financial Overview
  checkNewPage(40);
  yPosition += 10;
  addText('PANORAMICA FINANZIARIA', 14, true);
  yPosition += 5;

  if (ecofin) {
    const balanceSheetYear = ecofin.balanceSheetDate ? moment(ecofin.balanceSheetDate).year() : ecofin.turnoverYear;
    addText(`Anno di riferimento: ${balanceSheetYear}`);
    yPosition += 3;
    addText(`Fatturato: € ${new Intl.NumberFormat('it-IT').format(ecofin.turnover || 0)}`);
    addText(`Capitale Sociale: € ${new Intl.NumberFormat('it-IT').format(ecofin.shareCapital || 0)}`);
    addText(`Patrimonio Netto: € ${new Intl.NumberFormat('it-IT').format(ecofin.netWorth || 0)}`);
    addText(`Range Fatturato: ${ecofin.turnoverRange?.description || 'N/A'}`);
    addText(`Dimensione Impresa: ${ecofin.enterpriseSize?.description || 'N/A'}`);
  }

  // Operating Results
  const operatingResults = reportData.fullData?.operatingResults;
  if (operatingResults) {
    yPosition += 5;
    addText(`EBITDA: € ${new Intl.NumberFormat('it-IT').format(operatingResults.ebitda || 0)}`);
    addText(`EBIT: € ${new Intl.NumberFormat('it-IT').format(operatingResults.ebit || 0)}`);
    addText(`Cash Flow: € ${new Intl.NumberFormat('it-IT').format(operatingResults.cashFlow || 0)}`);
  }

  // Section: Employees
  const employees = reportData.fullData?.employees;
  if (employees) {
    checkNewPage(20);
    yPosition += 10;
    addText('DIPENDENTI', 14, true);
    yPosition += 5;
    addText(`Numero Dipendenti: ${employees.employee || 'N/A'}`);
  }

  // Section: Managers
  const managers = reportData.fullData?.managers;
  if (managers && managers.length > 0) {
    checkNewPage(30);
    yPosition += 10;
    addText('AMMINISTRATORI', 14, true);
    yPosition += 5;
    
    managers.forEach((manager) => {
      checkNewPage(10);
      const name = manager.companyName || `${manager.name || ''} ${manager.surname || ''}`.trim();
      const role = manager.roles?.[0]?.role?.description || 'N/A';
      addText(`• ${name} - ${role}`);
    });
  }

  // Section: Shareholders
  const shareholders = reportData.fullData?.shareholders;
  if (shareholders && shareholders.length > 0) {
    checkNewPage(30);
    yPosition += 10;
    addText('SOCI', 14, true);
    yPosition += 5;
    
    shareholders.forEach((shareholder) => {
      checkNewPage(10);
      const info = shareholder.shareholdersInformation?.[0];
      const name = info?.companyName || `${info?.name || ''} ${info?.surname || ''}`.trim() || 'N/A';
      addText(`• ${name}: ${shareholder.percentShare}%`);
    });
  }

  // Footer on last page
  yPosition = pageHeight - 15;
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'normal');
  // Use the report's completed date or last update date
  const reportDate = reportData.completedAt || reportData.fullData?.companyDetails?.lastUpdateDate || moment().format();
  pdf.text(`Dati aggiornati al: ${moment(reportDate).format('DD/MM/YYYY')}`, pageWidth / 2, yPosition, { align: 'center' });

  // Generate filename
  const companyName = companyDetails?.companyName || 'Report';
  const vatNumber = companyDetails?.vatCode || '';
  const currentDate = moment().format('DD-MM-YYYY');
  const fileName = `${companyName.replace(/\s+/g, '_')}_${vatNumber}_${currentDate}.pdf`;

  // Save the PDF
  pdf.save(fileName);
  
  return fileName;
};

/**
 * Generate PDF and return as Blob for sharing
 */
export const generatePDFBlob = (reportData) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 20;

  const companyDetails = reportData.fullData?.companyDetails;
  const ecofin = reportData.fullData?.ecofin;
  const address = reportData.fullData?.address;
  
  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      pdf.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to add text
  const addText = (text, fontSize = 10, isBold = false) => {
    pdf.setFontSize(fontSize);
    if (isBold) {
      pdf.setFont(undefined, 'bold');
    } else {
      pdf.setFont(undefined, 'normal');
    }
    pdf.text(text, 15, yPosition);
    yPosition += fontSize * 0.4;
  };

  // Company name as title
  pdf.setFontSize(20);
  pdf.setFont(undefined, 'bold');
  pdf.text(companyDetails?.companyName || 'Report Aziendale', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 15;

  // Section: Company Details
  addText('INFORMAZIONI AZIENDA', 14, true);
  yPosition += 5;

  if (companyDetails) {
    addText(`Partita IVA: ${companyDetails.vatCode || 'N/A'}`);
    addText(`Codice Fiscale: ${companyDetails.taxCode || 'N/A'}`);
    addText(`CCIAA: ${companyDetails.cciaa || 'N/A'}`);
    addText(`REA: ${companyDetails.reaCode || 'N/A'}`);
  }

  if (address) {
    yPosition += 5;
    addText(`Indirizzo: ${address.streetName || ''}, ${address.zipCode || ''} ${address.town || ''} (${address.province?.code || ''})`);
  }

  // Add company age if available
  const companyDates = reportData.fullData?.companyDates;
  if (companyDates) {
    const foundationDate = companyDates.incorporationDate || companyDates.registrationDate || companyDates.startDate;
    if (foundationDate) {
      const years = moment().diff(moment(foundationDate), 'years');
      yPosition += 5;
      addText(`Età Azienda: ${years} anni (fondata il ${moment(foundationDate).format('DD/MM/YYYY')})`);
    }
  }

  // Section: Financial Overview
  checkNewPage(40);
  yPosition += 10;
  addText('PANORAMICA FINANZIARIA', 14, true);
  yPosition += 5;

  if (ecofin) {
    const balanceSheetYear = ecofin.balanceSheetDate ? moment(ecofin.balanceSheetDate).year() : ecofin.turnoverYear;
    addText(`Anno di riferimento: ${balanceSheetYear}`);
    yPosition += 3;
    addText(`Fatturato: € ${new Intl.NumberFormat('it-IT').format(ecofin.turnover || 0)}`);
    addText(`Capitale Sociale: € ${new Intl.NumberFormat('it-IT').format(ecofin.shareCapital || 0)}`);
    addText(`Patrimonio Netto: € ${new Intl.NumberFormat('it-IT').format(ecofin.netWorth || 0)}`);
  }

  // Footer on last page
  yPosition = pageHeight - 15;
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'normal');
  const reportDate = reportData.completedAt || reportData.fullData?.companyDetails?.lastUpdateDate || moment().format();
  pdf.text(`Dati aggiornati al: ${moment(reportDate).format('DD/MM/YYYY')}`, pageWidth / 2, yPosition, { align: 'center' });

  // Generate filename
  const companyName = companyDetails?.companyName || 'Report';
  const vatNumber = companyDetails?.vatCode || '';
  const currentDate = moment().format('DD-MM-YYYY');
  const fileName = `${companyName.replace(/\s+/g, '_')}_${vatNumber}_${currentDate}.pdf`;

  // Return as Blob
  const pdfBlob = pdf.output('blob');
  
  return {
    blob: pdfBlob,
    fileName: fileName
  };
};